import { useCallback, useMemo, useRef, useState } from 'react';

import { iaRa } from 'lib/helpers/utility';
import { useBillingRenderingProviders } from 'lib/hooks/queries/billing/useBillingRenderingProviders';

import Button from 'components/shared/Button/Button';
import Tabs from 'components/shared/Tabs/Tabs';

import { renderInfo } from '../../lib/helpers';

import AddProviderButton from './components/AddProviderButton/AddProviderButton';
import AddProviderStep from './components/AddProviderButton/AddProviderStep';
import ProvidersTable from './components/ProvidersTable/ProvidersTable';

const AddProviderFooter = ({ selectionCountRef, onClose, isInNetworkTab, onSubmit }) => {
  const [selectionCount, setSelectionCount] = useState(0);

  selectionCountRef.current = setSelectionCount;

  return (
    <>
      <Button text="Close" size="small" style="outlined" color="neutral" onClick={onClose} />
      <Button
        text={`Add ${isInNetworkTab ? 'in-network' : 'out-of-network'} providers`}
        size="small"
        onClick={onSubmit}
        isDisabled={selectionCount === 0}
      />
    </>
  );
};

const InOutNetworkProviders = ({ values, practiceId, setFieldValue, openModal }) => {
  const addProviderStepRef = useRef();
  const selectionCountRef = useRef();
  const valuesRef = useRef(values);
  valuesRef.current = values;

  const [activeTab, setActiveTab] = useState(0);

  const { in_network_providers, out_of_network_providers } = values || {};

  const { data: billingAndRenderingProvidersData } = useBillingRenderingProviders({
    params: { practiceId }
  });

  const practitioners = useMemo(() => {
    const billingProviders = iaRa(billingAndRenderingProvidersData?.providers?.billing).map(
      (provider) => ({
        value: provider.id,
        label: provider.bill_name,
        type: 'billing',
        ...provider
      })
    );

    const renderingProviders = iaRa(billingAndRenderingProvidersData?.providers?.rendering).map(
      (provider) => ({
        value: provider.id,
        label: provider.fullName,
        type: 'rendering',
        ...provider
      })
    );

    return [...billingProviders, ...renderingProviders];
  }, [billingAndRenderingProvidersData]);

  const inNetworkProviders = practitioners.filter((p) => in_network_providers?.includes(p.value));
  const outOfNetworkProviders = practitioners.filter((p) =>
    out_of_network_providers?.includes(p.value)
  );

  const isInNetworkTab = activeTab === 0;

  const handleRemoveInNetworkProvider = (providerId) => {
    const current = valuesRef.current?.in_network_providers;
    const updatedIds = iaRa(current).filter((id) => id !== providerId);
    setFieldValue('in_network_providers', updatedIds);
  };

  const handleRemoveOutOfNetworkProvider = (providerId) => {
    const current = valuesRef.current?.out_of_network_providers;
    const updatedIds = iaRa(current).filter((id) => id !== providerId);
    setFieldValue('out_of_network_providers', updatedIds);
  };

  const openAddProviderModal = useCallback(
    (isInNetworkTab) => {
      if (!openModal) return;

      const currentFieldName = isInNetworkTab ? 'in_network_providers' : 'out_of_network_providers';
      const handleAddProviders = (selectedProviderIds) => {
        const currentIds = values?.[currentFieldName] || [];
        const updatedIds = [...new Set([...currentIds, ...selectedProviderIds])];
        setFieldValue(currentFieldName, updatedIds);
      };

      const AddProviderModalContent = ({ close }) => (
        <AddProviderStep
          ref={addProviderStepRef}
          breadcrumb={{ goBack: close }}
          practitioners={practitioners}
          onAdd={handleAddProviders}
          onSelectionCountChange={(count) => selectionCountRef.current?.(count)}
          isInNetwork={isInNetworkTab}
          selectedProviders={[...iaRa(in_network_providers), ...iaRa(out_of_network_providers)]}
        />
      );

      openModal({
        title: `Add ${isInNetworkTab ? 'in-network' : 'out-of-network'} providers`,
        content: AddProviderModalContent,
        onSubmit: () => addProviderStepRef.current?.handleAddProviders?.(),
        footer: ({ onClose }) => (
          <AddProviderFooter
            selectionCountRef={selectionCountRef}
            onClose={onClose}
            isInNetworkTab={isInNetworkTab}
            onSubmit={() => addProviderStepRef.current?.handleAddProviders?.()}
          />
        )
      });
    },
    [in_network_providers, out_of_network_providers, practitioners]
  );

  const addProviderButton = (
    <AddProviderButton onOpenAddProvider={openAddProviderModal} isInNetworkTab={isInNetworkTab} />
  );

  const tabsData = useMemo(
    () => [
      {
        label: 'In-network providers',
        length: inNetworkProviders.length,
        lengthContainerClassName: `${isInNetworkTab ? 'bg-primary-50' : ''} rounded-full border border-solid border-neutral-300`,
        lengthClassName: '!text-neutral-700 !font-500',
        content: (
          <ProvidersTable
            providers={inNetworkProviders}
            onRemove={handleRemoveInNetworkProvider}
            fieldName="in_network_providers"
            addProviderButton={addProviderButton}
          />
        )
      },
      {
        label: 'Out of network providers',
        length: outOfNetworkProviders.length,
        lengthContainerClassName: `${!isInNetworkTab ? 'bg-primary-50' : ''} rounded-full border border-solid border-neutral-300`,
        lengthClassName: '!text-neutral-700 !font-500',
        content: (
          <ProvidersTable
            providers={outOfNetworkProviders}
            onRemove={handleRemoveOutOfNetworkProvider}
            fieldName="out_of_network_providers"
            addProviderButton={addProviderButton}
          />
        )
      }
    ],
    [inNetworkProviders, outOfNetworkProviders, isInNetworkTab, addProviderButton]
  );

  return (
    <>
      <div className="col-span-3">
        <div className="mb-6 mt-2 h-[1px] w-full bg-neutral-200" />

        {renderInfo(
          'In-network & out of network providers',
          'Define which providers are in-network or out-of-network to determine coverage and patient cost responsibility.'
        )}
      </div>

      <div className="col-span-3 overflow-hidden rounded-lg border border-solid border-neutral-200">
        <Tabs
          tabsData={tabsData}
          tabIndex={activeTab}
          setTabIndex={setActiveTab}
          hasContent
          theme="secondary"
          className="px-[10px] py-2"
          rightActions={addProviderButton}
        />
      </div>
    </>
  );
};

export default InOutNetworkProviders;
